import React, { useEffect, useState } from 'react';
import qs from 'qs';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Responsive } from '~/components';
import {
  getSeasonFiltersRequest,
  getCompetitionFiltersRequest,
  getClubFiltersRequest,
  getStadiumFiltersRequest,
  getCarouselDatesFiltersRequest,
} from '~/store/modules/filters/actions';
import history from '~/config/history';
import { Mobile, Desktop } from './responsive';

export default function DashboardFilters() {
  const dispatch = useDispatch();
  const { search, pathname } = useLocation();
  const query = qs.parse(search, {
    ignoreQueryPrefix: true,
  });

  const { clubs, competitions, seasons, stadiums, carouselDates } = useSelector(
    state => state.filters.filters
  );

  const [seasonFilter, setSeasonFilter] = useState(null);
  const [competitionFilter, setCompetitionFilter] = useState(null);
  const [clubFilter, setClubFilter] = useState(null);
  const [dateFilter, setDateFilter] = useState(null);
  const [stadiumFilter, setStadiumFilter] = useState(null);
  const [carouselDatesFilter, setCarouselDatesFilter] = useState(
    query['gameweek-date'] ? query['gameweek-date'] : null
  );
  const [currentGameweekIndex, setCurrentGameweekIndex] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(null);
  const [sliderRef, setSliderRef] = useState(null);

  const getSlideRef = ref => {
    setSliderRef(ref);
  };

  useEffect(() => {
    // sets season filter to the default if none is queried
    if (!seasonFilter && !query.season && seasons.data.length > 0) {
      setSeasonFilter(seasons.data.filter(season => season.current));
    }
    // sets season filter to the queried season
    if (!seasonFilter && query.season && seasons.data.length > 0) {
      setSeasonFilter(
        seasons.data.filter(season => {
          return query.season === season.id.toString();
        })
      );
    }
  }, [seasonFilter, setSeasonFilter, seasons, query.season]);

  useEffect(() => {
    // whenever the query changes, new filters are requested
    batch(() => {
      dispatch(getSeasonFiltersRequest(search));
      dispatch(getCompetitionFiltersRequest(search));
      dispatch(getClubFiltersRequest(search));
      dispatch(getStadiumFiltersRequest(search));
      dispatch(getCarouselDatesFiltersRequest(search));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    // whenever any filter changes, the query must change
    if (
      seasonFilter ||
      competitionFilter ||
      clubFilter ||
      stadiumFilter ||
      carouselDatesFilter
    ) {
      history.push({
        pathname,
        search: qs.stringify(
          {
            season: seasonFilter && seasonFilter[0]?.id,
            championship: competitionFilter && competitionFilter[0]?.id,
            team: clubFilter && clubFilter[0]?.id,
            venue: stadiumFilter && stadiumFilter[0]?.id,
            // CHECK pass the id
            'gameweek-date':
              carouselDatesFilter === '' ? null : carouselDatesFilter,
          },
          {
            skipNulls: true,
          }
        ),
      });
    }
  }, [
    pathname,
    seasonFilter,
    competitionFilter,
    clubFilter,
    stadiumFilter,
    carouselDatesFilter,
  ]);

  // Extract current round of carousel dates whenever the carousel changes
  useEffect(() => {
    let currentRoundIndex = null;
    carouselDates.data.gameweeks.forEach(({ current }, index) => {
      if (current) {
        currentRoundIndex = index;
      }
    });
    setCurrentGameweekIndex(currentRoundIndex || 0);
  }, [carouselDates, setCurrentGameweekIndex]);

  // Season filter changes
  useEffect(() => {
    setCompetitionFilter(null);
    setClubFilter(null);
    setStadiumFilter(null);
    setDateFilter(null);
    setCarouselDatesFilter(null);
  }, [
    seasonFilter,
    setCompetitionFilter,
    setClubFilter,
    setStadiumFilter,
    setDateFilter,
    setCarouselDatesFilter,
  ]);

  // Competition filter changes
  useEffect(() => {
    setClubFilter(null);
    setStadiumFilter(null);
    setDateFilter(null);
    setCarouselDatesFilter(null);
  }, [
    competitionFilter,
    setClubFilter,
    setStadiumFilter,
    setDateFilter,
    setCarouselDatesFilter,
  ]);

  // Club filter changes
  useEffect(() => {
    setStadiumFilter(null);
    setDateFilter(null);
    setCarouselDatesFilter(null);
  }, [clubFilter, setStadiumFilter, setDateFilter, setCarouselDatesFilter]);

  // Stadium filter changes
  useEffect(() => {
    setDateFilter(null);
    setCarouselDatesFilter(null);
  }, [stadiumFilter, setDateFilter, setCarouselDatesFilter]);

  useEffect(() => {
    if (!dateFilter) {
      setCarouselDatesFilter(null);
    }
  }, [dateFilter, setCarouselDatesFilter]);

  useEffect(() => {
    if (
      sliderRef &&
      carouselDates.data.dates.length > 0 &&
      currentGameweekIndex
    ) {
      if (!dateFilter) {
        if (sliderRef && sliderRef.slickGoTo) {
          sliderRef.slickGoTo(currentGameweekIndex);
        }
        setCurrentSlide(currentGameweekIndex);
      } else {
        let slideToGoTo = null;
        carouselDates.data.gameweeks.forEach(({ date }, index) => {
          if (date === dateFilter) {
            slideToGoTo = index;
          }
        });

        if (slideToGoTo) {
          if (sliderRef && sliderRef.slickGoTo) {
            sliderRef.slickGoTo(slideToGoTo);
          }
          setCurrentSlide(slideToGoTo);
          setCarouselDatesFilter(
            carouselDates.data.gameweeks[slideToGoTo].date
          );
        }
      }
    }
  }, [
    sliderRef,
    currentGameweekIndex,
    carouselDates,
    dateFilter,
    setCurrentSlide,
    setCarouselDatesFilter,
  ]);

  const resetFilters = () => {
    setSeasonFilter(seasons.data.filter(season => season.current));
    setCompetitionFilter(null);
    setClubFilter(null);
    setStadiumFilter(null);
    setDateFilter(null);

    let slideToGoTo = 0;
    carouselDates.data.gameweeks.forEach(({ current }, index) => {
      if (current) {
        slideToGoTo = index;
      }
    });
    setCarouselDatesFilter(null);
    if (sliderRef && sliderRef.slickGoTo) sliderRef.slickGoTo(slideToGoTo);
    setCurrentSlide(slideToGoTo);
  };

  const renderProps = {
    clubs,
    competitions,
    seasons,
    dates: carouselDates.data.dates,
    stadiums,
    carouselDates,
    sliderRef,
    getSlideRef,
    resetFilters,
    clubFilter,
    competitionFilter,
    seasonFilter,
    dateFilter,
    stadiumFilter,
    carouselDatesFilter,
    setSeasonFilter,
    setCompetitionFilter,
    setClubFilter,
    setStadiumFilter,
    setDateFilter,
    setCarouselDatesFilter,
    currentSlide,
    setCurrentSlide,
  };

  return (
    <Responsive
      md={<Mobile {...renderProps} />}
      lg={<Desktop {...renderProps} />}
    />
  );
}
